import { create } from 'zustand';
import { notice } from './notice.store.js';

export type ToastTone = 'success' | 'error' | 'info';

export interface Toast {
  id: string;
  tone: ToastTone;
  title: string;
  description?: string;
  /** Milliseconds before auto-dismiss; 0 keeps it until dismissed. */
  duration: number;
  /** Shown without the success sound: for a screen that has already played its own. */
  quiet?: boolean;
}

interface ToastState {
  toasts: Toast[];
  push(toast: Omit<Toast, 'id' | 'duration'> & { duration?: number }): string;
  dismiss(id: string): void;
  clear(): void;
}

const MAX_VISIBLE = 3;

export const useToastStore = create<ToastState>((set) => ({
  toasts: [],

  push: (input) => {
    const id = `t-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const toast: Toast = {
      id,
      tone: input.tone,
      title: input.title,
      ...(input.description !== undefined ? { description: input.description } : {}),
      ...(input.quiet ? { quiet: true } : {}),
      duration: input.duration ?? (input.tone === 'error' ? 5000 : 3000),
    };
    // Oldest falls off rather than letting a burst of toasts cover the screen.
    set((state) => ({ toasts: [...state.toasts, toast].slice(-MAX_VISIBLE) }));
    return id;
  },

  dismiss: (id) => set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) })),
  clear: () => set({ toasts: [] }),
}));

/** Imperative helpers so services and mutations can raise a toast directly. */
export const toast = {
  success: (title: string, description?: string, options?: { quiet?: boolean }) =>
    useToastStore.getState().push({
      tone: 'success',
      title,
      ...(description ? { description } : {}),
      ...(options?.quiet ? { quiet: true } : {}),
    }),
  /*
   * A failure is not a footnote. Every call site in the app already reports one
   * through here, so this is where the painted slab takes over: the toast stays
   * for what is merely worth knowing, and anything that went wrong stops the
   * screen until it has been read.
   */
  error: (title: string, description?: string) => {
    notice.error(title, description);
    return '';
  },
  info: (title: string, description?: string) =>
    useToastStore.getState().push({ tone: 'info', title, ...(description ? { description } : {}) }),
};
